import type {
  EntityBody,
  SimulationEntityState,
  TransitionRecording,
} from "@cloudnest/stone-flicking-core-engine/types";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { resolveAsfCanvasTokens } from "../utils/asf-token-bridge";
import {
  renderBoard,
  renderEntities,
  renderFlickPreview,
  renderReadyIndicators,
} from "../utils/canvas-renderer";
import type { CircularCanvasLayout } from "../utils/coordinate-transform";

interface AnimationState {
  readonly recording: TransitionRecording;
  readonly startTime: number;
}

const FRAME_DURATION_MS = 1000 / 60;

export function useCanvasRenderer(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  layout: CircularCanvasLayout | null,
  entities: ReadonlyArray<EntityBody>,
  selectedEntityId: string | null,
  playerCount: number,
  readyPlayers: ReadonlyArray<string>,
  flickPreview: {
    fromX: number;
    fromY: number;
    toX: number;
    toY: number;
  } | null,
  animationRecording: TransitionRecording | null,
  onAnimationComplete: () => void,
) {
  const animationRef = useRef<AnimationState | null>(null);
  const animFrameRef = useRef<number>(0);

  const entitiesById = useMemo(
    () => new Map(entities.map((e) => [e.id, e])),
    [entities],
  );

  const render = useCallback(
    (overrideEntities?: ReadonlyArray<EntityBody>, timestamp?: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !layout) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // WHY: resolve tokens once per draw — Canvas 2D API only takes literal
      // colors, but the active light/dark theme can change between draws.
      const tokens = resolveAsfCanvasTokens(canvas);

      ctx.clearRect(0, 0, layout.canvasWidth, layout.canvasHeight);
      renderBoard(ctx, layout, playerCount, tokens);

      const entitiesToRender = overrideEntities ?? entities;
      renderEntities(
        ctx,
        entitiesToRender,
        layout,
        selectedEntityId,
        tokens,
        timestamp,
      );

      renderReadyIndicators(ctx, layout, playerCount, readyPlayers, tokens);

      if (flickPreview) {
        renderFlickPreview(
          ctx,
          flickPreview.fromX,
          flickPreview.fromY,
          flickPreview.toX,
          flickPreview.toY,
          layout,
          tokens,
        );
      }
    },
    [
      canvasRef,
      layout,
      entities,
      selectedEntityId,
      playerCount,
      readyPlayers,
      flickPreview,
    ],
  );

  useEffect(() => {
    if (!animationRecording || !layout) {
      animationRef.current = null;
      render();
      return;
    }

    animationRef.current = {
      recording: animationRecording,
      startTime: performance.now(),
    };

    function animate(now: number) {
      const animationState = animationRef.current;
      if (!animationState) return;

      const elapsed = now - animationState.startTime;
      const frameIndex = Math.min(
        Math.floor(elapsed / FRAME_DURATION_MS),
        animationState.recording.frames.length - 1,
      );

      const frame = animationState.recording.frames[frameIndex];
      if (frame) {
        const frameEntities: EntityBody[] = frame.entities.map(
          (fe: SimulationEntityState) => {
            const original = entitiesById.get(fe.id);
            return {
              id: fe.id,
              archetype: original?.archetype ?? "STONE",
              owner: original?.owner ?? "P1",
              x: fe.x,
              y: fe.y,
              radius: original?.radius ?? 0.35,
              mass: original?.mass ?? 1,
              eliminated: fe.eliminated,
            };
          },
        );

        render(frameEntities, now);
      }

      if (frameIndex >= animationState.recording.frames.length - 1) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = 0;
        animationRef.current = null;
        onAnimationComplete();
        return;
      }

      animFrameRef.current = requestAnimationFrame(animate);
    }

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [animationRecording, layout, entitiesById, render, onAnimationComplete]);

  // Static re-render when no animation is playing
  useEffect(() => {
    if (!animationRecording && !selectedEntityId) {
      render(undefined, performance.now());
    }
  }, [render, animationRecording, selectedEntityId]);

  // Continuous render loop for selection pulse animation
  const pulseFrameRef = useRef<number>(0);

  useEffect(() => {
    if (animationRecording || !selectedEntityId) {
      if (pulseFrameRef.current) {
        cancelAnimationFrame(pulseFrameRef.current);
        pulseFrameRef.current = 0;
      }
      return;
    }

    function pulseLoop(now: number) {
      render(undefined, now);
      pulseFrameRef.current = requestAnimationFrame(pulseLoop);
    }

    pulseFrameRef.current = requestAnimationFrame(pulseLoop);

    return () => {
      if (pulseFrameRef.current) {
        cancelAnimationFrame(pulseFrameRef.current);
        pulseFrameRef.current = 0;
      }
    };
  }, [animationRecording, selectedEntityId, render]);
}
